import {Reducer, updateFactory} from '@modifi/redux'
import {isEmpty, pick} from 'ramda'
import {DocumentTypeVal} from '@modifi/enums'
import {
  aUserFeedBackSubmitSuccessful,
  TRADE_PARTNER_OUTSTANDING_BALANCE,
  TRADE_PARTNER_TOTAL_EXPORT_VOLUME,
  TRADE_PARTNER_TRADING_SINCE,
  TRADE_PARTNER_CONTACT_PERSON_EMAIL,
  TRADE_PARTNER_CONTACT_PERSON_FIRST_NAME,
  TRADE_PARTNER_CONTACT_PERSON_LAST_NAME,
  TRADE_PARTNER_CONTACT_PERSON_PHONE,
  TRADE_PARTNER_CONTACT_PERSON_SALUTATION,
  TRADE_PARTNER_HAS_DELAY_IN_PAYMENTS,
  TRADE_PARTNER_MAXIMUM_DELAY_IN_DAYS,
  TRADE_PARTNER_CONTACT_PERSON_POSITION,
  TRADE_PARTNER_EXPECTED_FINANCING_LIMIT,
  TRADE_PARTNER_TRADING_CURRENCY,
} from '@modifi/plugin-onboarding'
import {getAuthenticatedThemisClient} from '@modifi/onboarding-api-clients'
import {aPush, generatePath} from '@modifi/router'
import {aCloseDialog} from '@modifi/plugin-dialogs'

import {
  aFetchSellerTradePartnerDocumentsByTradePartnerId,
  aFetchSellerTradePartners,
  aUpdateLocalSellerTradePartner,
  selectSellerTradePartnerByExternalId,
  selectSellerTradePartnerDocumentsByTradePartnerId,
  selectSellerTradePartnerContactPersons,
} from '../../data/seller-trade-partners'
import {aFetchInputFields} from '../../data/input-fields'
import {TRADE_PARTNER_TRANSPORT_DOCUMENT_TYPES} from './constants'
import {
  mapTradePartnerToFormData,
  formatContactPersonChanges,
  formatExistingPersonsForPutRequest,
} from './utils'
import {getLogger} from '../../utils'
import {
  mapFormDataToRequest,
  shouldRenderOutstandingVolumeFilter,
  shouldRenderTotalExportVolumeFilter,
  shouldRenderTradingSinceFilter,
} from './components/company-data/utils'
import {SECTION_SELLER_TRADE_PARTNERS} from '../../constants/sections'
import {ROUTE_COMPANY_TRADE_PARTNERS_BY_ID} from '../../routes/constants'
import {aSetCurrentStep} from './steps-reducer'

const logger = getLogger('trade-partner-data-page')

const apiClient = getAuthenticatedThemisClient()

type DocumentChange = {
  added: {fileId: string; documentType: DocumentTypeVal}[]
  removed: string[]
}

export const selectInitialFormData = (
  state: Record<string, any>,
  tradePartnerId?: string
): {initialValues: Record<string, any>; defaultValues: Record<string, any>} => {
  const tradePartner = tradePartnerId
    ? selectSellerTradePartnerByExternalId(state, tradePartnerId)
    : undefined
  const tradePartnerDocuments = tradePartnerId
    ? selectSellerTradePartnerDocumentsByTradePartnerId(state, tradePartnerId)
    : undefined

  const initialValues: Record<string, any> = {
    transportDocuments: [],
    sampleDocuments: [],
  }
  if (tradePartner) {
    Object.assign(
      initialValues,
      mapTradePartnerToFormData({
        ...tradePartner,
        ...tradePartner?.tradePartnerCompany,
        ...tradePartner?.tradePartnerCompany?.address,
      })
    )
  }

  if ((tradePartner?.tradePartnerCompany?.users?.length || 0) > 0) {
    Object.assign(
      initialValues,
      tradePartner?.tradePartnerCompany?.users?.reduce(
        (result, user, index) => ({
          ...result,
          [`${TRADE_PARTNER_CONTACT_PERSON_EMAIL}-${index}`]: user?.email,
          [`${TRADE_PARTNER_CONTACT_PERSON_SALUTATION}-${index}`]: user?.salutation,
          [`${TRADE_PARTNER_CONTACT_PERSON_FIRST_NAME}-${index}`]: user?.firstName,
          [`${TRADE_PARTNER_CONTACT_PERSON_LAST_NAME}-${index}`]: user?.lastName,
          [`${TRADE_PARTNER_CONTACT_PERSON_PHONE}-${index}`]: user?.phone,
          [`${TRADE_PARTNER_CONTACT_PERSON_POSITION}-${index}`]: user?.position,
        }),
        {}
      )
    )
  }

  if (tradePartnerDocuments && tradePartnerDocuments?.length > 0) {
    Object.assign(
      initialValues,
      tradePartnerDocuments.reduce(
        (result, document) => {
          if (document.documentType) {
            if (TRADE_PARTNER_TRANSPORT_DOCUMENT_TYPES.includes(document.documentType)) {
              result.transportDocuments.push(pick(['documentType', 'fileId'], document))
            } else {
              result.sampleDocuments.push(pick(['documentType', 'fileId'], document))
            }
          }

          return result
        },
        {transportDocuments: [], sampleDocuments: []} as {
          transportDocuments: any[]
          sampleDocuments: any[]
        }
      )
    )
  }

  return {
    initialValues,
    defaultValues: {},
  }
}

const {actions, selectors, reducer} = updateFactory({
  actionNamePrefix: 'pages/trade-partner-details/submit',
  rootSelector: state => state?.pages?.sellerTradePartners?.form,
  submitRequest:
    (
      a,
      externalTradePartnerId: string,
      changes: Record<string, any>,
      contactPersonsToDelete: string[]
    ) =>
    async (dispatch, getState): Promise<void> => {
      logger.info('Submitting page for tradePartner=%s', externalTradePartnerId)
      logger.debug('changes: %o', changes)

      if (changes?.hasDeletedContactPersons) {
        // Deleting this value since not to be sent to BE and it was just
        // an indicator to form state that there are contacts that are deleted

        // eslint-disable-next-line no-param-reassign
        delete changes.hasDeletedContactPersons
      }
      let contactPersonsChanges = {}

      const filteredChanges = Object.keys(changes).reduce((updatedChanges, changeKey) => {
        if (changeKey?.includes('contactPerson')) {
          contactPersonsChanges = {
            ...contactPersonsChanges,
            [changeKey]: changes[changeKey],
          }
          return updatedChanges
        }
        return {
          ...updatedChanges,
          [changeKey]: changes[changeKey],
        }
      }, {} as Record<string, any>)

      const formattedContactPersonChanges = formatContactPersonChanges(contactPersonsChanges)
      const existingContactPersons = externalTradePartnerId
        ? selectSellerTradePartnerContactPersons(getState(), externalTradePartnerId)
        : []

      const {sampleDocuments, transportDocuments, ...companyChanges} = filteredChanges
      const documentChanges: DocumentChange[] = Object.values({
        sampleDocuments,
        transportDocuments,
      }).filter(Boolean)

      dispatch(a.pendingAction())
      let createdExternalTradePartnerId

      // new company
      if (!externalTradePartnerId) {
        try {
          createdExternalTradePartnerId = await apiClient.TradePartners.create({
            body: mapFormDataToRequest(changes, true),
          })
            ?.then(res => res?.data())
            .then(tradePartner => tradePartner?.tradePartnerCompany?.externalCompanyId)
        } catch (e) {
          dispatch(aCloseDialog())
          logger.error(`Error occurred: %o`, e)
          dispatch(a.errorAction(e))
          return
        }
      }

      try {
        if (
          (isEmpty(companyChanges) &&
            formattedContactPersonChanges?.length === 0 &&
            contactPersonsToDelete?.length === 0) ||
          !externalTradePartnerId
        ) {
          logger.info(`No company related changes detected - skipping`)
        } else {
          logger.info(`Updating company...`)
          // make sure certain fields are unset correctly
          const prevTradePartner = selectInitialFormData(getState(), externalTradePartnerId)
          const merged = {
            ...prevTradePartner.defaultValues,
            ...prevTradePartner.initialValues,
            ...companyChanges,
          }

          if (!shouldRenderTradingSinceFilter(merged)) {
            Object.assign(companyChanges, {[TRADE_PARTNER_TRADING_SINCE]: undefined})
          }
          if (!shouldRenderOutstandingVolumeFilter(merged)) {
            Object.assign(companyChanges, {[TRADE_PARTNER_OUTSTANDING_BALANCE]: undefined})
          }
          if (!shouldRenderTotalExportVolumeFilter(merged)) {
            Object.assign(companyChanges, {[TRADE_PARTNER_TOTAL_EXPORT_VOLUME]: undefined})
          }

          if (
            // eslint-disable-next-line no-prototype-builtins
            companyChanges.hasOwnProperty(TRADE_PARTNER_HAS_DELAY_IN_PAYMENTS) &&
            !companyChanges?.[TRADE_PARTNER_HAS_DELAY_IN_PAYMENTS]
          ) {
            Object.assign(companyChanges, {[TRADE_PARTNER_MAXIMUM_DELAY_IN_DAYS]: undefined})
          }

          if (
            // eslint-disable-next-line no-prototype-builtins
            companyChanges.hasOwnProperty(TRADE_PARTNER_EXPECTED_FINANCING_LIMIT) ||
            // eslint-disable-next-line no-prototype-builtins
            companyChanges.hasOwnProperty(TRADE_PARTNER_TRADING_CURRENCY)
          ) {
            // the limit is sent as an amount object, so both fields have to be sent
            // even when only one of them was changed
            Object.assign(companyChanges, {
              [TRADE_PARTNER_EXPECTED_FINANCING_LIMIT]:
                merged[TRADE_PARTNER_EXPECTED_FINANCING_LIMIT],
              [TRADE_PARTNER_TRADING_CURRENCY]: merged[TRADE_PARTNER_TRADING_CURRENCY],
            })
          }

          const body = mapFormDataToRequest(companyChanges)
          if (formattedContactPersonChanges?.length > 0 || contactPersonsToDelete?.length > 0) {
            await apiClient.TradePartners.updateUsers({
              externalBuyerTradePartnerId: externalTradePartnerId,
              body: {
                users: [
                  ...formattedContactPersonChanges,
                  ...formatExistingPersonsForPutRequest(
                    existingContactPersons,
                    contactPersonsToDelete
                  ),
                ],
              },
            })
          }

          // optimistic update
          dispatch(
            aUpdateLocalSellerTradePartner({
              ...companyChanges,
              company: {
                ...(companyChanges?.company || null),
                externalCompanyId: externalTradePartnerId,
              },
            })
          )

          const updatedTradePartner = await apiClient.TradePartners.update({
            externalBuyerTradePartnerId: externalTradePartnerId,
            body,
          }).then(res => res.data())

          dispatch(
            aUpdateLocalSellerTradePartner(
              {
                ...updatedTradePartner,
                company: {
                  ...(updatedTradePartner.company || null),
                  externalCompanyId: externalTradePartnerId,
                },
              },
              false
            )
          )
        }
      } catch (e: any) {
        logger.error(`Error occurred: %o`, e)
        dispatch(a.errorAction(e))
        return
      }

      try {
        if (isEmpty(documentChanges)) {
          logger.info('No document related changed detected - skipping')
        } else {
          logger.info('Linking & Unlinking documents...')
          const mergedChanges = documentChanges.reduce(
            (result, change: DocumentChange) => {
              if (change.added && change.added.length > 0) {
                result.added.push(...change.added)
              }
              if (change.removed && change.removed.length > 0) {
                result.removed.push(...change.removed)
              }
              return result
            },
            {added: [], removed: []} as DocumentChange
          )

          await Promise.all([
            ...mergedChanges.added.map(file =>
              apiClient.TradePartners.attachDocument({
                externalBuyerTradePartnerId: externalTradePartnerId,
                body: file,
              })
            ),
            ...mergedChanges.removed.map(fileId =>
              apiClient.TradePartners.detachDocument({
                externalBuyerTradePartnerId: externalTradePartnerId,
                fileId,
              })
            ),
          ])

          await dispatch(aFetchSellerTradePartnerDocumentsByTradePartnerId(externalTradePartnerId))
        }
      } catch (e: any) {
        logger.error(`Error occurred: %o`, e)
        dispatch(a.errorAction(e))
        return
      } finally {
        await Promise.all([dispatch(aFetchSellerTradePartners()), dispatch(aFetchInputFields())])
      }

      dispatch(a.successAction())
      if (createdExternalTradePartnerId) {
        // this is done so that after creation in seller trade partner page ,
        // user can automatically move to step 2
        dispatch(aSetCurrentStep(2))
        await dispatch(aFetchSellerTradePartners())
        await dispatch(aFetchInputFields())
        dispatch(
          aPush(
            generatePath(ROUTE_COMPANY_TRADE_PARTNERS_BY_ID, {
              externalId: createdExternalTradePartnerId,
            }),
            {
              showSuccessDialog: true,
            }
          )
        )
      }
      dispatch(aCloseDialog())
      dispatch(aUserFeedBackSubmitSuccessful(SECTION_SELLER_TRADE_PARTNERS))
    },
})

export const {aSubmit: aSavePage, aReset: aClearError} = actions

export default reducer as Reducer

export const {
  selectUpdatePending: selectSavePending,
  selectUpdateErrorObject: selectSaveError,
  selectUpdateErrorMessage: selectSaveErrorMessage,
  selectUpdateErrorFieldErrors: selectSaveErrorFieldErrors,
} = selectors
